import React from 'react';
import clsx from 'clsx';
import { gql } from '@soundxyz/gql-string';
import { useSafeLayoutEffect } from '@components-shared/src/hooks/useSafeLayoutEffect';
import { LoadingSkeleton } from '@components/basics/LoadingSkeleton';
import { Location } from '@components/basics/Location';
import { DescriptionModal } from '@components/modals/DescriptionModal';
import { ModalContext, ModalType } from '@context/modal';
import type { FragmentType } from '@graphql/reactQuery';
import { AboutSectionFragmentDoc, getFragment } from '@graphql/reactQuery';
import { JoinedAt } from './components/JoinedAt';

const MOBILE_MAX_HEIGHT = 54; //max 3 lines (18px * 3)

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment AboutSection on User {
    id
    username
    createdAt
    description
    artist {
      id
      name
    }
    location {
      label
    }
  }
`);

export const AboutSection = ({
  aboutData,
  loading,
}: {
  aboutData: FragmentType<AboutSectionFragmentDoc>;
  loading?: boolean;
}) => {
  const aboutInfo = getFragment(AboutSectionFragmentDoc, aboutData);

  const { username, description, artist, location, createdAt } = aboutInfo;

  const artistName = artist?.name;

  const displayName = !!artistName ? artistName : username;

  const isEmptyProfile = !location && !createdAt && !description;

  const bioRef = React.useRef<HTMLParagraphElement>(null);

  const [showToggle, setShowToggle] = React.useState<boolean>(false);

  const { openModal } = ModalContext.useContainer();

  useSafeLayoutEffect(() => {
    if (!bioRef.current) return;
    const bioHeight = bioRef.current.clientHeight;
    if (bioHeight > MOBILE_MAX_HEIGHT) {
      setShowToggle(true);
    }
  }, [bioRef]);

  const hasBottomInfo = !!description;

  return (
    <div
      className={clsx(
        'w-full px-5 py-6 md:py-12 lt:px-0 dt:pb-0',
        isEmptyProfile ? 'flex w-full px-5 md:hidden' : 'flex',
      )}
    >
      <div className="flex w-full flex-col gap-6 md:gap-4 lg:max-w-2xl">
        <div className="flex flex-col gap-2">
          <LoadingSkeleton loading={!!loading} className="h-[60px] w-52">
            <>
              <h2 className="font-title text-title-m font-medium md:text-title-l">
                About {displayName}
              </h2>
              <div className="flex flex-wrap gap-x-5 gap-y-1 font-base text-base-m font-medium text-base400 md:gap-x-6">
                {location?.label && (
                  <Location
                    location={location?.label}
                    loading={loading}
                    className="whitespace-nowrap font-base text-base-m font-medium leading-6 text-base400"
                  />
                )}
                <JoinedAt createdAt={createdAt} loading={loading} className="leading-6" />
              </div>
            </>
          </LoadingSkeleton>
        </div>
        {hasBottomInfo && (
          <LoadingSkeleton loading={!!loading} className="h-4 w-10/12 lg:w-80">
            <div className="flex flex-col gap-2">
              <p
                ref={bioRef}
                className={clsx(
                  'font-base text-base-m font-normal text-base800 md:text-base-l lg:max-h-[unset]',
                  showToggle
                    ? 'max-h-[54px] overflow-hidden lg:max-h-[unset] lg:overflow-visible'
                    : 'overflow-visible',
                )}
              >
                {description}
              </p>
              {showToggle && (
                <>
                  <div className="absolute top-0 h-[54px] w-full bg-gradient-to-t from-white via-50% lg:hidden" />
                  <button
                    onClick={() => {
                      openModal(ModalType.ARTIST_BIO_MODAL, {
                        body: <DescriptionModal name="About" description={description} />,
                      });
                    }}
                    className="mb-2 w-fit font-base text-base-m font-medium text-base400 outline-none lg:hidden"
                  >
                    read more
                  </button>
                </>
              )}
            </div>
          </LoadingSkeleton>
        )}
      </div>
    </div>
  );
};
